import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Virtualizer } from "@tanstack/react-virtual";
import JobLogGroupStore from "~/stores/JobLogGroupStore";
import { Job } from "~/stores/JobLogStore";
import { FailureMarker } from "~/components/job/Job/Log/useTestFailureMarkers";
import type { LogLine, GroupLogLine } from "~/components/job/Job/Log/Output/VirtualisedLogOutput";

/**
 * Manages scroll behaviour and visibility tracking for test failure markers
 * in the virtualised log output.
 *
 * Responsibilities:
 * 1. **Auto-scroll** — when markers first load, scrolls to the earliest failure
 *    (opening its collapsed group if needed). Skipped if the URL already targets
 *    a specific line or the user has manually scrolled.
 * 2. **Disable auto-follow** — synchronously turns off the "follow tail" mode
 *    before the browser paints, preventing a scroll-to-bottom race.
 * 3. **Visibility tracking** — derives which marker is currently visible in
 *    the viewport (1-based index) from the virtualizer's range.
 * 4. **Cycle navigation** — exposes `scrollToNextFailure` so the UI can step
 *    through markers sequentially.
 * 5. **Line lookup** — returns `failureLineNumbers`, a Map from line number to
 *    its FailureMarker, used by the Line component to render gutter indicators.
 */
export function useFailureMarkerScroll({
  markers,
  markersLoading,
  lines,
  log,
  logLineNumber,
  following,
  onDisableFollow,
  onFailureMarkersChange,
  rowVirtualizer,
}: {
  markers: FailureMarker[];
  markersLoading: boolean;
  lines: Array<LogLine | GroupLogLine>;
  log: Job;
  logLineNumber: number | null | undefined;
  following: boolean | undefined;
  onDisableFollow: (() => void) | undefined;
  onFailureMarkersChange?: (
    info: { count: number; current?: number; scrollToNext: () => number } | null,
  ) => void;
  rowVirtualizer: Virtualizer<any, any>;
}) {
  const failureLineNumbers = useMemo(() => {
    const map = new Map<number, FailureMarker>();
    for (const marker of markers) {
      for (let line = marker.startLine; line <= marker.endLine; line++) {
        map.set(line, marker);
      }
    }
    return map;
  }, [markers]);

  // Track whether we've already scrolled to the first failure marker for this job
  const hasScrolledToFailure = useRef<string | null>(null);

  // Track whether the user has manually scrolled the log. useScrollFollow
  // already detects user-initiated scrolls (wheel, scrollbar drag, keyboard)
  // and cancels following, so we use that signal — once following has been
  // cancelled it means the user took control of the scroll position.
  const userHasScrolled = useRef(false);

  useEffect(() => {
    // following starts as true (auto-follow) and becomes false when the user
    // scrolls by any means. Once cancelled, latch it — we don't reset even
    // if following is re-enabled.
    if (following === false) {
      userHasScrolled.current = true;
    }
  }, [following]);

  // Reset when the job changes
  useEffect(() => {
    userHasScrolled.current = false;
  }, [log.id]);

  // Keep refs so callbacks always read fresh state without re-creating
  const markersRef = useRef(markers);
  markersRef.current = markers;

  // Store a pending marker to scroll to. When a group needs expanding first,
  // the scroll is deferred until an effect sees the target line appear in
  // `lines` — avoiding the previous setTimeout(0) race where React may not
  // have re-rendered the expanded group in time.
  const [pendingScrollMarker, setPendingScrollMarker] = useState<FailureMarker | null>(null);

  const scrollToMarker = useCallback((marker: FailureMarker) => {
    if (marker.groupId) {
      JobLogGroupStore.set(marker.groupId, true);
    }

    // Set the pending marker — the effect below will perform the scroll once
    // the target line is present in `lines` (which may be immediate if the
    // group is already expanded, or after a re-render if it was just opened).
    setPendingScrollMarker(marker);
  }, []);

  // Perform the scroll when the target line appears in `lines`.
  useEffect(() => {
    if (!pendingScrollMarker) {
      return;
    }

    const virtualIndex = lines.findIndex((line) => line.number === pendingScrollMarker.startLine);
    if (virtualIndex !== -1) {
      rowVirtualizer.scrollToIndex(Math.max(0, virtualIndex - 1), { align: "start" });
      setPendingScrollMarker(null);
    }
  }, [pendingScrollMarker, lines]);

  /**
   * Disable auto-follow synchronously when failure markers are available.
   *
   * useLayoutEffect ensures the setState (follow=false) triggers a synchronous
   * re-render before the browser paints, so useScrollFollow's useEffect never
   * runs with follow=true — preventing the scroll-to-bottom that would race
   * with our scroll-to-failure below.
   */
  useLayoutEffect(() => {
    if (markersLoading || logLineNumber || hasScrolledToFailure.current === log.id) {
      return;
    }
    if (userHasScrolled.current || markers.length === 0) {
      return;
    }
    onDisableFollow?.();
  }, [markersLoading, markers, logLineNumber, log.id, onDisableFollow]);

  /**
   * Scroll to the first test failure marker when markers become available,
   * unless the URL already targets a specific line or the user has scrolled.
   */
  useEffect(() => {
    if (markersLoading || logLineNumber || hasScrolledToFailure.current === log.id) {
      return;
    }

    // Don't fight with the user if they've already scrolled
    if (userHasScrolled.current) {
      hasScrolledToFailure.current = log.id;
      return;
    }

    hasScrolledToFailure.current = log.id;

    if (markers.length > 0) {
      scrollToMarker(markers[0]);
    }
  }, [markersLoading, markers, scrollToMarker, logLineNumber, lines, log.id]);

  // Derive the topmost visible failure marker (1-based) from the virtualizer's
  // visible range. Only recomputes when the range changes (not every scroll
  // pixel), but reads scroll position imperatively to accurately exclude rows
  // hidden behind the sticky log header or in the overscan zone.
  const startIndex = rowVirtualizer.range?.startIndex;
  const endIndex = rowVirtualizer.range?.endIndex;

  const visibleMarkerIndex = useMemo(() => {
    if (startIndex == null || endIndex == null || markers.length === 0) {
      return undefined;
    }

    const scrollElement = rowVirtualizer.scrollElement;
    if (!scrollElement) {
      return undefined;
    }

    const viewportStart = scrollElement.scrollTop;
    const viewportEnd = viewportStart + scrollElement.clientHeight;
    const scrollMargin = rowVirtualizer.options.scrollMargin;

    const virtualItems = rowVirtualizer.getVirtualItems();
    const visibleLines = virtualItems
      .filter((item) => {
        const itemStart = item.start - scrollMargin;
        const itemEnd = itemStart + item.size;
        return itemEnd > viewportStart && itemStart < viewportEnd;
      })
      .map((item) => lines[item.index])
      .filter((line) => line && line.type !== "group");

    const idx = markers.findIndex((marker) =>
      visibleLines.some((line) => line.number >= marker.startLine && line.number <= marker.endLine),
    );
    return idx === -1 ? undefined : idx + 1;
  }, [markers, lines, startIndex, endIndex]);

  const visibleMarkerRef = useRef<number | undefined>();
  visibleMarkerRef.current = visibleMarkerIndex;

  // Cycle to the next failure marker relative to the currently visible one.
  const scrollToNextFailure = useCallback((): number => {
    const currentMarkers = markersRef.current;

    if (currentMarkers.length === 0) {
      return 0;
    }

    // If a marker is visible, advance past it; otherwise start at the first
    const current = visibleMarkerRef.current; // 1-based or undefined
    const nextIndex = current !== undefined ? current % currentMarkers.length : 0;
    scrollToMarker(currentMarkers[nextIndex]);
    return nextIndex + 1;
  }, [scrollToMarker]);

  useEffect(() => {
    if (!onFailureMarkersChange) {
      return;
    }

    if (markers.length > 0) {
      onFailureMarkersChange({
        count: markers.length,
        current: visibleMarkerIndex,
        scrollToNext: scrollToNextFailure,
      });
    } else {
      onFailureMarkersChange(null);
    }
  }, [markers, visibleMarkerIndex, scrollToNextFailure, onFailureMarkersChange]);

  return { failureLineNumbers };
}
